import _ from 'lodash';
import {
  get,
  post,
  put,
  del, } from 'utils/fetch';
import { 
  createAction,
  entity_reducer,
  list_reducer,
  list_remove } from 'utils/store';
import { Storage, RequestState } from 'constants/constants';

import { selectApiHost, selectApiToken } from 'store/AppState';
import { SESSION_PING } from 'store/models/Session';
import { selectCurrentAccountId, selectAccount, getStreamerByChannelId } from 'store/models/Account';


export const FETCH_OVERLAYS = 'FETCH_OVERLAYS';
export const CREATE_OVERLAY = 'CREATE_OVERLAY';
export const UPDATE_OVERLAY = 'UPDATE_OVERLAY';
export const DELETE_OVERLAY = 'DELETE_OVERLAY';

export const FETCH_SLIDESHOW = 'FETCH_SLIDESHOW';

export const ORDER_SCENES = 'ORDER_SCENES';

export const FETCH_HEALTH_CHECK = 'FETCH_HEALTH_CHECK';

export const ADD_SCENE = 'ADD_SCENE';
export const ADD_CHANNEL_SCENE = 'ADD_CHANNEL_SCENE';
export const REMOVE_SCENE = 'REMOVE_SCENE';
export const REMOVE_CHANNEL_SCENE = 'REMOVE_CHANNEL_SCENE';
export const UPDATE_CHANNEL_SETTINGS = 'UPDATE_CHANNEL_SETTINGS';


// ======================
// HELPERS
// ====================== 
export const createTitle = user => {
  let name = user.username;
  let title = `${_.capitalize(name)}'s Overlay`;
  return title;
}

export const overlayUrl = (host, user, overlay, streamer) => {
  let base = `${host}/o/${user?.username}/${overlay?.code}`;
  base = _.isString(streamer) && streamer.length 
    ? base + `/${streamer}` 
    : base;
  return base;
}

export const diffSceneSettings = (sceneSettings, baseSettings) => sceneSettings
  |> _.mapValues(#, v => _.pick(v, ['sceneId', 'settings']))
  |> _.mapValues(#, s => _.set(s, 'settings', _.omitBy(s.settings, (v, k) => v === baseSettings[k])))
  

export const defaultOverlay = overlays => _.orderBy(overlays, 'createdAt', 'desc')?.[0];


// ======================
// SELECTORS
// ====================== 
export const selectOverlay = (state, overlayId) => state.models.overlays[overlayId];
export const selectDefaultOverlay = (state) => state.models.overlays |> _.values |> _.first;
export const selectOverlays = (state) => state.models.overlays;
export const selectOverlayByCode = (state, code) => _.find(state.models.overlays, {code});
export const selectOverlaySceneIds = (state, id) => _.chain(selectOverlay(state, id))
  .get('scenes')
  .map('sceneId')
  .value();

export const selectSlideshow = (state, code, streamer = '') => state.models.slideshows[code + streamer];
export const selectHealthCheck = (state, overlayId, channelId) => state.models.healthChecks[overlayId + channelId];



// ======================
// ENTITY
// ====================== 

const Entity = {
  actions: {

    fetchOverlay: (accountId) => (dispatch, getState) => 
      accountId ?? selectCurrentAccountId(getState())
      |> get({
        url: "/api/account/:accountId/overlay",
        params: { accountId: # },
        dispatch,
        action: FETCH_OVERLAYS,
      }),
    
    fetchOverlays: (accountId) => (dispatch, getState) => 
      accountId ?? selectCurrentAccountId(getState())
      |> get({
        url: "/api/account/:accountId/overlays",
        params: { accountId: # },
        dispatch,
        action: FETCH_OVERLAYS,
      }),

    getOverlayById: (overlayId, range) => (dispatch, getState) => get({
      url: "/api/overlay/:overlayId", 
      params: { overlayId },
      dispatch, 
      action: FETCH_OVERLAYS,
    }),

    getOverlayByCode: code => (dispatch, getState) => get({
      url: `/api/overlay/code/:code`,
      params: { code },
      dispatch, 
      action: FETCH_OVERLAYS,
    }),

    getOverlayById: overlayId => (dispatch, getState) => get({
      url: `/api/overlay/:overlayId`,
      params: { overlayId },
      dispatch, 
      action: FETCH_OVERLAYS,
    }),

    fetchOverlaysByAccount: accountId => (dispatch, getState) => {
      let accountId = accountId ?? selectCurrentAccountId(getState());
      return get({
        url: "/api/account/:accountId/overlays", 
        params: { accountId },
        dispatch, 
        action: FETCH_OVERLAYS,
      })
    },

    getHealthCheck: (overlayId, channelId) => (dispatch, getState) =>
      get({
        url: "/api/overlay/:overlayId/channel/:channelId/health_check",
        params: { overlayId, channelId },
        dispatch,
        action: FETCH_HEALTH_CHECK,
      }),

    setupOverlay: values => (dispatch, getState) => 
      post({
        url: "/api/overlay/setup", 
        body: values,
        dispatch, 
        action: CREATE_OVERLAY,
      }),

    createOverlay: (values) => (dispatch, getState) => { 
      let accountId = selectCurrentAccountId(getState())
      return put({
        url: "/api/account/:accountId/overlay/create",
        params: { accountId },
        body: { values },
        dispatch, 
        action: CREATE_OVERLAY,
      });
    },

    updateOverlay: (id, values) => {
      return (dispatch, getState) => put({
        url: "/api/overlay/:id",
        params: { id },
        body: values,
        dispatch, 
        action: UPDATE_OVERLAY,
        host: selectApiHost(getState()),
      });
    },

    deleteOverlay: (overlayId) => (dispatch, getState) => 
      del({
        url: "/api/overlay/:overlayId",
        params: { overlayId },
        dispatch, 
        action: DELETE_OVERLAY,
      }),

    getSlideshow: (overlayId, channelId, query) => {
      return (dispatch, getState) => get({
        url: "/api/overlay/:overlayId/slideshow",
        params: { overlayId, channelId, ...query},
        dispatch,
        action: FETCH_SLIDESHOW,
        host: selectApiHost(getState()),
      })
    },

    updateSettings: (overlayId, settings) => {
      return (dispatch, getState) => put({
        url: "/api/overlay/:overlayId/settings",
        params: { overlayId },
        body: { settings, },
        dispatch,
        action: UPDATE_OVERLAY,
        host: selectApiHost(getState()),
      });
    },


    // ====================
    // ====================
    // ====================
    updateSceneSettings: (overlayId, settings, channelId) => {
      return (dispatch, getState) => put({
        url: "/api/overlay/:overlayId/scene_settings",
        params: { overlayId, channelId },
        body: { settings, },
        dispatch,
        action: UPDATE_OVERLAY,
        host: selectApiHost(getState()),
      });
    },

    updateSceneOrder: (overlayId, sceneOrders, channelId) => {
      return (dispatch, getState) => put({
        url: "/api/overlay/:overlayId/scene_order",
        params: { overlayId, channelId },
        body: { sceneOrders },
        dispatch,
        action: ORDER_SCENES,
        host: selectApiHost(getState()),
      });
    },

    addScene: (overlayId, sceneId, channelId) => {
      return (dispatch, getState) => put({
        url: "/api/overlay/:overlayId/scene/:sceneId",
        params: { overlayId, sceneId, channelId },
        dispatch, 
        action: ADD_SCENE,
        host: selectApiHost(getState()),
      });
    },

    removeScene: (overlayId, sceneId, channelId) => {
      return (dispatch, getState) => del({
        url: "/api/overlay/:overlayId/scene/:sceneId",
        params: { overlayId, sceneId, channelId },
        dispatch, 
        action: REMOVE_SCENE,
        host: selectApiHost(getState()),
      });
    },
  },

  spec: {
    overlays: {},
    slideshows: {},
    healthChecks: {},
  },
  reducer: (state, type, body, action, json) => {
    if (action.url && action.result !== RequestState.SUCCESS) 
      return state;
    
    if(body && body.overlays) {
      state = { ...state, 
        overlays: {
          ...state.overlays,
          ..._.keyBy(body.overlays, '_id'),
        }
      }
    }

    switch (type) {
      case FETCH_SLIDESHOW: {
        let overlayId = action.params.overlayId;
        let channelId = action.params.channelId;
        let overlay = _.find(state.overlays, { _id: overlayId });
        let account = _.find(state.accounts, { _id: overlay.accountId });
        let code = overlay?.code;
        let streamer = getStreamerByChannelId(account, channelId);
        return {
          ...state,
          slideshows: {
            ...state.slideshows,
            [code + streamer]: {
              sceneIds: _.map(body.sceneSettings, 'sceneId'),
              overlaySettings: body.overlaySettings,
              sceneSettings: _.keyBy(body.sceneSettings, 'sceneId'),
              channelSettings: body.channelSettings,
              defaultScenes: body.defaultScenes,
              scenesByCampaign: body.scenesByCampaign,
              campaignsByScene: body.campaignsByScene,
            }
          }
        }
      }
      case SESSION_PING: {
        let { code, streamer = '' } = action.params;
        return {
          ...state,
          slideshows: {
            ...state.slideshows,
            [code + streamer]: {
              sceneIds: _.map(body.sceneSettings, 'sceneId'),
              overlaySettings: body.overlaySettings,
              sceneSettings: _.keyBy(body.sceneSettings, 'sceneId'),
              channelSettings: body.channelSettings,
              baseSettings: body.baseSettings,
            }
          }
        }
      }
      case FETCH_HEALTH_CHECK:
        return {
          ...state, 
          healthChecks: {...state.healthChecks,
            [action.params.overlayId+action.params.channelId]: body.healthCheck,
          }
        }
      case DELETE_OVERLAY :
        return {
          ...state,
          overlays: _.omit(state.overlays, action.params.overlayId),
        }
    }
    return state;
  },
}
_.bindAll(Entity.actions, _.functions(Entity.actions));
export default Entity;
